import { type TFunction } from "i18next";
import { ColorV2 } from "config/colors";
import { type GetTimeReportsV2SummaryResponse } from "services/api/company/timeReports.service";
import {
  type TimeReportsSummaryCard,
  type TimeReportsSummaryCardId,
} from "./components/common/summaryCards/timeReportsSummaryCards.types";

interface SummaryCardDefinition {
  id: TimeReportsSummaryCardId;
  filter: TimeReportsSummaryCard["filter"];
  labelKey: string;
  iconName: string;
  color: ColorV2;
  getValue: (
    summary: GetTimeReportsV2SummaryResponse | null,
  ) => string | undefined;
}

interface BuildTimeReportsSummaryCardsProps {
  activeSummaryFilter: TimeReportsSummaryCard["filter"] | null;
  formatSummaryTime: (value?: string) => string;
  summary: GetTimeReportsV2SummaryResponse | null;
  t: TFunction;
}

const summaryCardDefinitions: SummaryCardDefinition[] = [
  {
    id: "planned",
    filter: "Planned",
    labelKey: "TimeReports.Planned",
    iconName: "v2/calendar",
    color: ColorV2.Blue1,
    getValue: (summary) => summary?.planned.time,
  },
  {
    id: "approved",
    filter: "Approved",
    labelKey: "TimeReports.Approved",
    iconName: "v2/checkmark",
    color: ColorV2.PositiveGreen,
    getValue: (summary) => summary?.approved.time,
  },
  {
    id: "toApprove",
    filter: "Pending",
    labelKey: "TimeReports.ToApprove",
    iconName: "v2/clock",
    color: ColorV2.ElectricBlue,
    getValue: (summary) => summary?.pending.time,
  },
  {
    id: "unreported",
    filter: "Open",
    labelKey: "TimeReports.Unreported",
    iconName: "v2/document",
    color: ColorV2.DarkAmbigiousYellow,
    getValue: (summary) => summary?.open.time,
  },
  {
    id: "absence",
    filter: "Absence",
    labelKey: "TimeReports.Absence",
    iconName: "v2/cross-person",
    color: ColorV2.AbsentPurple,
    getValue: (summary) => summary?.absence.time,
  },
  {
    id: "deviations",
    filter: "Deviation",
    labelKey: "TimeReports.Deviations",
    iconName: "v2/absent",
    color: ColorV2.NegativeRed,
    getValue: (summary) => summary?.deviation.time,
  },
];

/** Maps summary response data into summary card view models for the time reports header. */
export const buildTimeReportsSummaryCards = (
  props: BuildTimeReportsSummaryCardsProps,
): TimeReportsSummaryCard[] => {
  return summaryCardDefinitions.map((cardDefinition) => {
    return {
      id: cardDefinition.id,
      filter: cardDefinition.filter,
      label: props.t(cardDefinition.labelKey),
      value: props.formatSummaryTime(cardDefinition.getValue(props.summary)),
      iconName: cardDefinition.iconName,
      color: cardDefinition.color,
      isHighlighted: props.activeSummaryFilter === cardDefinition.filter,
    };
  });
};
